import { getAllOption, getBgColor } from '@library/component-utils/filters';
import { Float } from '@headlessui-float/react';
import { Listbox } from '@headlessui/react';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import type { SelectOption } from '@library/types/utils';
import { SelectOptions } from '@library/components/selects';
import arrowDownIcon from '@library/assets/images/icons/arrow-down.svg';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';

export interface SelectProps<T extends string> {
  allOption?: boolean;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  iconOnly?: boolean;
  label: string;
  offset?: number;
  onChange: (value: SelectOption<T>) => void;
  options: Array<SelectOption<T>>;
  optionsClassName?: string;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  size: 'small' | 'medium' | 'large';
  testId?: string;
  value: string;
  zIndex?: number;
  portal?: boolean;
}

export const Select = <T extends string>({
  allOption = false,
  className = '',
  containerClassName = '',
  disabled = false,
  iconOnly = false,
  label,
  offset = 0,
  onChange,
  options,
  optionsClassName = '',
  placement = 'bottom-start',
  size,
  testId = 'select',
  value,
  zIndex = undefined,
  portal = false
}: SelectProps<T>) => {
  const smallSize = size === 'small';
  const mediumSize = size === 'medium';
  const largeSize = size === 'large';
  const allSelectOption = getAllOption(label);
  const selectOptions = allOption ? [allSelectOption, ...options] : options;

  if (!selectOptions.length) {
    return null;
  }

  const selectedOption = value
    ? selectOptions.find((option) => (option.value === value ? option : null))
    : allSelectOption;

  const {
    icon: selectedIcon,
    name: selectedName,
    display
  } = selectedOption ?? allSelectOption;

  const disableSelect = disabled || !options.length;

  return (
    <div
      className={classNames(
        'relative',
        smallSize ? 'h-10 max-w-[20rem]' : '',
        mediumSize ? 'h-11 max-w-[20rem]' : '',
        largeSize ? 'h-11' : '',
        iconOnly ? 'group/proj-stat min-w-[17px]' : '',
        containerClassName
      )}
      data-testid={`${testId}-select`}
    >
      <Listbox disabled={disabled} value={selectedOption} onChange={onChange}>
        {({ open }) => {
          return (
            <Float
              zIndex={zIndex ?? 9}
              offset={offset}
              placement={placement}
              strategy="absolute"
              portal={portal}
            >
              <Listbox.Button
                className={classNames(
                  smallSize ? 'text-soClean-140' : 'text-soClean-100',
                  getBgColor({ open, disabled: disableSelect }),
                  'border-soFresh-140 relative min-h-[40px] rounded-md border py-2 pl-3 text-left sm:text-sm',
                  selectedIcon ? 'pr-16' : 'pr-10',
                  disableSelect
                    ? 'cursor-default'
                    : 'hover:bg-soFresh-120 cursor-pointer',
                  smallSize ? 'h-10 max-w-[20rem]' : '',
                  mediumSize ? 'h-11 max-w-[20rem]' : '',
                  largeSize ? 'h-11 max-w-[26.875rem]' : '',
                  iconOnly
                    ? 'group-focus-within/proj-stat:border-soFresh-150 group-hover/proj-stat:!border-soFresh-150 flex h-5 !min-h-0 !min-w-0 items-center border-transparent bg-transparent !px-0 group-focus-within/proj-stat:!pr-5 group-hover/proj-stat:!pr-5'
                    : '',
                  iconOnly && open
                    ? '!bg-soFresh-120 !border-soFresh-150 !pr-5'
                    : '',
                  className
                )}
                data-testid={`${testId}-select-button`}
              >
                <span
                  className={classNames(
                    'flex items-center whitespace-nowrap',
                    smallSize ? 'text-xs' : 'text-base'
                  )}
                >
                  {selectedIcon}
                  {!iconOnly && (display ?? selectedName)}
                  {selectedOption?.oddsType && (
                    <OddsTypeIndicator
                      oddsType={selectedOption.oddsType}
                      className="pl-2"
                      iconClassName="size-3"
                    />
                  )}
                </span>

                <span
                  className={classNames(
                    'pointer-events-none absolute inset-y-0 right-0 items-center',
                    iconOnly
                      ? 'hidden group-focus-within/proj-stat:flex group-hover/proj-stat:flex'
                      : 'flex pr-2',
                    iconOnly && open ? '!flex' : ''
                  )}
                >
                  <img
                    className={classNames(open ? 'rotate-180' : '')}
                    src={smallSize ? arrowDownIcon : chevronDownIcon}
                    alt="Chevron Down icon"
                  />
                </span>
              </Listbox.Button>
              <Listbox.Options
                className={classNames(
                  largeSize ? 'w-[26.875rem]' : 'w-max',
                  'bg-soFresh-120 ring-soFresh-100 focus:outline-hidden absolute z-10 mt-1 max-h-60 overflow-auto rounded-md py-1 text-base shadow-lg ring-1 sm:text-sm',
                  optionsClassName
                )}
              >
                <SelectOptions
                  options={selectOptions}
                  smallSize={smallSize}
                  testId={testId}
                />
              </Listbox.Options>
            </Float>
          );
        }}
      </Listbox>
    </div>
  );
};
